import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, ShieldAlert, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ChangePassword() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const forced = !!user?.force_password_change;

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("Passwords do not match"); return; }
    if (next === current) { toast.error("New password must be different from the current one"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated successfully");
      await refreshUser();
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-9 h-9 rounded-md bg-brand-blue flex items-center justify-center">
              <KeyRound className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-bold text-slate-900">Create New Password</h1>
              <p className="text-xs text-slate-500">Secure your account with a personal password</p>
            </div>
          </div>

          {forced && (
            <div className="flex items-start gap-2 mt-4 p-3 rounded-md bg-amber-50 border border-amber-200"
                 data-testid="force-change-notice">
              <ShieldAlert className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-amber-800 leading-snug">
                An administrator has reset your password. Please create a new one before you can continue.
              </div>
            </div>
          )}

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="change-password-form">
            <label className="block">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">
                Current {forced ? "(temporary) " : ""}password
              </div>
              <input type="password" autoFocus required value={current}
                onChange={(e) => setCurrent(e.target.value)}
                data-testid="cp-current"
                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-blue-800" />
            </label>
            <label className="block">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">New password</div>
              <input type="password" required minLength={6} value={next}
                onChange={(e) => setNext(e.target.value)}
                data-testid="cp-new"
                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-blue-800" />
              <div className="text-[11px] text-slate-400 mt-1">Minimum 6 characters. Choose something only you know.</div>
            </label>
            <label className="block">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Confirm new password</div>
              <input type="password" required minLength={6} value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="cp-confirm"
                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-blue-800" />
            </label>
            <button type="submit" disabled={busy}
              data-testid="cp-submit"
              className="w-full py-2.5 rounded-md bg-brand-blue bg-brand-blue-hover text-white font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              Update Password
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
